// assets/range-input.js

class RangeInput extends HTMLElement {
  /**
   * @constructor
   * Initializes the RangeInput component.
   */
  constructor() {
    super();

    /** @const {number} Width of the thumb in pixels */
    this.THUMB_WIDTH = 24;
    this.DEBOUNCE_DELAY = 300;

    this.trackWidth = 0;
    this.resizeObserver = null;

    this.addListeners();
  }

  /**
   * Called when the element is added to the DOM.
   */
  connectedCallback() {
    this.addListeners();
    this.update();
    this.setInputValue();
    this.setRangeValue();

    this.trackWidth = this.rangeInput.getBoundingClientRect().width;
  }

  /**
   * Adds event listeners for input changes and resizing.
   */
  addListeners() {
    this.rangeInput.addEventListener("input", () => this.handleRangeInputChange());
    this.input.addEventListener("input", debounce(() => this.handleInputChange(this.input.value), this.DEBOUNCE_DELAY));
    this.resizeObserver = new ResizeObserver(() => this.update());
    this.resizeObserver.observe(this.rangeInput);

    if (this.form) this.form.addEventListener("reset", () => setTimeout(() => this.update()));
  }

  /**
   * Updates the track width and input position.
   */
  update() {
    this.trackWidth = this.rangeInput.getBoundingClientRect().width;
    this.updateInputPosition();
  }

  /**
   * Handles changes to the range input.
   */
  handleRangeInputChange() {
    this.setInputValue();
    this.updateInputPosition();
  }

  /**
   * Handles changes to the text input.
   */
  handleInputChange() {
    this.setRangeValue();
    this.updateInputPosition();
  }

  /**
   * Sets the text input value to match the range input.
   */
  setInputValue() {
    this.input.value = this.rangeInput.value;
  }

  /**
   * Sets the range input value to match the text input.
   */
  setRangeValue() {
    if (this.input.value) this.rangeInput.value = this.input.value;
  }

  /**
   * Updates the position of the input wrapper based on the range value.
   */
  updateInputPosition() {
    const min = this.rangeInput.min ? this.rangeInput.min : 0;
    const max = this.rangeInput.max ? this.rangeInput.max : 100;
    const value = this.rangeInput.value;
    const percentage = ((value - min) / (max - min)) * 100;
    const left = percentage * (this.trackWidth - this.THUMB_WIDTH) / 100;

    this.inputWrapper.style.left = `${left}px`;
  }

  /**
   * Resets the range input to its initial value.
   */
  reset() {
    this.rangeInput.value = "0";
  }

  /**
   * @returns {HTMLInputElement} The range input element
   */
  get rangeInput() {
    return this.querySelector("[data-range-input]");
  }

  /**
   * @returns {HTMLInputElement} The text input element
   */
  get input() {
    return this.querySelector("[data-input]");
  }

  /**
   * @returns {HTMLElement} The input wrapper element
   */
  get inputWrapper() {
    return this.querySelector("[data-input-wrapper]");
  }

  /**
   * Returns the quiz form element.
   * @returns {HTMLFormElement}
   */
  get form() {
    return this.closest("[data-form]");
  }
}

if (!customElements.get('range-input')) {
  customElements.define('range-input', RangeInput);
}